/**
 * Tenant Hook - Multi-tenant support
 * მრავალ-ტენანტის მხარდაჭერა
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Tenant } from '../types';

interface TenantState {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  setCurrentTenant: (tenant: Tenant | null) => void;
  setTenants: (tenants: Tenant[]) => void;
  setLoading: (loading: boolean) => void;
  switchTenant: (tenantId: string) => void;
  addTenant: (tenant: Tenant) => void;
  updateTenant: (tenant: Tenant) => void;
  removeTenant: (tenantId: string) => void;
  clear: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      tenants: [],
      isLoading: false,

      setCurrentTenant: (tenant) => set({ currentTenant: tenant }),

      setTenants: (tenants) => {
        set({ tenants });
        // If no current tenant is set, select the first one
        if (!get().currentTenant && tenants.length > 0) {
          set({ currentTenant: tenants[0] });
        }
      },

      setLoading: (isLoading) => set({ isLoading }),

      switchTenant: (tenantId) => {
        const tenant = get().tenants.find((t) => t.id === tenantId);
        if (tenant) {
          set({ currentTenant: tenant });
        }
      },

      addTenant: (tenant) => {
        set((state) => ({
          tenants: [...state.tenants, tenant],
          currentTenant: state.currentTenant || tenant,
        }));
      },

      updateTenant: (tenant) => {
        set((state) => ({
          tenants: state.tenants.map((t) => (t.id === tenant.id ? tenant : t)),
          currentTenant: state.currentTenant?.id === tenant.id ? tenant : state.currentTenant,
        }));
      },

      removeTenant: (tenantId) => {
        set((state) => {
          const newTenants = state.tenants.filter((t) => t.id !== tenantId);
          return {
            tenants: newTenants,
            currentTenant: state.currentTenant?.id === tenantId
              ? newTenants[0] || null
              : state.currentTenant,
          };
        });
      },

      clear: () => set({ currentTenant: null, tenants: [], isLoading: false }),
    }),
    {
      name: 'tenant-storage',
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        tenants: state.tenants,
      }),
    }
  )
);

/**
 * useTenant hook - access tenant state
 */
export function useTenant() {
  const {
    currentTenant,
    tenants,
    isLoading,
    setCurrentTenant,
    setTenants,
    setLoading,
    switchTenant,
    addTenant,
    updateTenant,
    removeTenant,
    clear,
  } = useTenantStore();

  return {
    currentTenant,
    tenants,
    isLoading,
    setCurrentTenant,
    setTenants,
    setLoading,
    switchTenant,
    addTenant,
    updateTenant,
    removeTenant,
    clear,
    hasTenant: !!currentTenant,
    tenantId: currentTenant?.id,
  };
}
